import { GameScene } from '../GameScene';
import {
  ClientMessageType,
  type MarketAlertsMessage,
  type GoldUpdatedMessage,
  type OpenMarketplaceMessage,
  type OpenExchangeMessage,
  type MarketplaceSearchMessage,
  type MarketplacePublishMessage,
  type MarketplaceBuyMessage,
  type MarketplaceCancelMessage,
  type MarketplaceAppraiseMessage,
  type MarketplaceSaveSearchMessage,
  type MarketplaceDeleteSearchMessage,
  type MarketplaceDealsMessage,
  type ExchangeAddWatchMessage,
  type ExchangeRemoveWatchMessage,
  type MarketAlertsSeenMessage,
  type RequestTraderStatsMessage,
  type ExchangeViewItemMessage,
  type ExchangeCreateOrderMessage,
  type ExchangeCancelOrderMessage,
  type ExchangeClaimAllMessage,
} from '@shared/types/messages';
import { MarketSide } from '@shared/types/market';
import type { MarketplaceFilters, WatchDirection } from '@shared/types/market';
import { formatThousands } from '../../sprites/IconAtlases';
import { GOLD_TEXT_COLOR } from '@shared/constants';

/**
 * Cliente de red de la economía (Marketplace + Exchange): wrappers finos sobre colyseusClient.send()
 * + handlers de alertas/oro que refrescan los paneles. Extraído de GameScene (god-object). Accede a
 * la escena (colyseusClient, paneles) vía `this.scene.*`. Ver docs/ECONOMY_EXCHANGE_MARKETPLACE.md.
 */
export class MarketNetwork {
  constructor(private scene: GameScene) {}

  sendOpenMarketplace(npcId: string): void {
    if (!this.scene.colyseusClient) return;
    const msg: OpenMarketplaceMessage = { type: ClientMessageType.OPEN_MARKETPLACE, npcId };
    this.scene.colyseusClient.send(msg);
  }

  sendOpenExchange(npcId: string): void {
    if (!this.scene.colyseusClient) return;
    const msg: OpenExchangeMessage = { type: ClientMessageType.OPEN_EXCHANGE, npcId };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceSearch(filters: MarketplaceFilters): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceSearchMessage = { type: ClientMessageType.MARKETPLACE_SEARCH, npcId, filters };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplacePublish(slotIndex: number, price: number): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplacePublishMessage = { type: ClientMessageType.MARKETPLACE_PUBLISH, npcId, slotIndex, price };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceBuy(listingId: string): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceBuyMessage = { type: ClientMessageType.MARKETPLACE_BUY, npcId, listingId };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceCancel(listingId: string): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceCancelMessage = { type: ClientMessageType.MARKETPLACE_CANCEL, npcId, listingId };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceAppraise(slotIndex: number): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceAppraiseMessage = { type: ClientMessageType.MARKETPLACE_APPRAISE, npcId, slotIndex };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceSaveSearch(name: string, filters: MarketplaceFilters, alert: boolean): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceSaveSearchMessage = { type: ClientMessageType.MARKETPLACE_SAVE_SEARCH, npcId, name, filters, alert };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceDeleteSearch(searchId: string): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceDeleteSearchMessage = { type: ClientMessageType.MARKETPLACE_DELETE_SEARCH, npcId, searchId };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketplaceDeals(filters: MarketplaceFilters): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketplaceDealsMessage = { type: ClientMessageType.MARKETPLACE_DEALS, npcId, filters };
    this.scene.colyseusClient.send(msg);
  }

  sendExchangeAddWatch(itemId: string, direction: WatchDirection, targetPrice: number): void {
    const npcId = this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: ExchangeAddWatchMessage = { type: ClientMessageType.EXCHANGE_ADD_WATCH, npcId, itemId, direction, targetPrice };
    this.scene.colyseusClient.send(msg);
  }

  sendExchangeRemoveWatch(watchId: string): void {
    const npcId = this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: ExchangeRemoveWatchMessage = { type: ClientMessageType.EXCHANGE_REMOVE_WATCH, npcId, watchId };
    this.scene.colyseusClient.send(msg);
  }

  sendMarketAlertsSeen(): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId() ?? this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: MarketAlertsSeenMessage = { type: ClientMessageType.MARKET_ALERTS_SEEN, npcId };
    this.scene.colyseusClient.send(msg);
  }

  sendRequestTraderStats(): void {
    const npcId = this.scene.marketplacePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: RequestTraderStatsMessage = { type: ClientMessageType.REQUEST_TRADER_STATS, npcId };
    this.scene.colyseusClient.send(msg);
  }

  /** IDs de alertas ya mostradas en la consola (para no re-spamear en cada sync). */
  private shownAlertIds: Set<string> = new Set();

  handleMarketAlerts(message: MarketAlertsMessage): void {
    // Mostrar en la consola del sistema las alertas nuevas no vistas (una sola vez).
    for (const a of message.alerts) {
      if (a.seen || this.shownAlertIds.has(a.id)) continue;
      this.shownAlertIds.add(a.id);
      this.scene.chat.addSystemMessage(a.message, GOLD_TEXT_COLOR);
    }
    // Actualizar el contador del panel abierto.
    this.scene.marketplacePanel?.applyAlerts(message.alerts);
    this.scene.exchangePanel?.applyAlerts(message.alerts);
  }

  sendExchangeViewItem(itemId: string): void {
    const npcId = this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: ExchangeViewItemMessage = {
      type: ClientMessageType.EXCHANGE_VIEW_ITEM,
      npcId,
      itemId,
    };
    this.scene.colyseusClient.send(msg);
  }

  sendExchangeCreateOrder(
    itemId: string,
    side: MarketSide,
    price: number,
    quantity: number
  ): void {
    const npcId = this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: ExchangeCreateOrderMessage = {
      type: ClientMessageType.EXCHANGE_CREATE_ORDER,
      npcId,
      itemId,
      side: side === MarketSide.BUY ? 'buy' : 'sell',
      price,
      quantity,
    };
    this.scene.colyseusClient.send(msg);
  }

  sendExchangeCancelOrder(orderId: string): void {
    const npcId = this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: ExchangeCancelOrderMessage = {
      type: ClientMessageType.EXCHANGE_CANCEL_ORDER,
      npcId,
      orderId,
    };
    this.scene.colyseusClient.send(msg);
  }

  sendExchangeClaimAll(): void {
    const npcId = this.scene.exchangePanel?.getCurrentNpcId();
    if (!this.scene.colyseusClient || !npcId) return;
    const msg: ExchangeClaimAllMessage = {
      type: ClientMessageType.EXCHANGE_CLAIM_ALL,
      npcId,
    };
    this.scene.colyseusClient.send(msg);
  }

  handleGoldUpdated(message: GoldUpdatedMessage): void {
    this.scene.localGold = message.gold;
    this.scene.merchantPanel?.setGold(message.gold);
    this.scene.exchangePanel?.setGold(message.gold);
    this.scene.marketplacePanel?.setGold(message.gold);
    this.scene.spellVendorPanel?.setGold(message.gold);
    this.scene.inventoryPanel?.setGold(message.gold);
    // Bubble overhead "+N" / "-N" sobre el propio player. El bubble dorado
    // ya implica que el delta es de oro — no hace falta la palabra "oro".
    if (this.scene.localSessionId && message.delta !== undefined && message.delta !== 0) {
      const sign = message.delta > 0 ? '+' : '-';
      this.scene.overheadText.showChatBubble(
        this.scene.localSessionId,
        `${sign}${formatThousands(Math.abs(message.delta))}`,
        GOLD_TEXT_COLOR,
      );
    }
  }
}
